import { useState } from "react";
import { api } from "../api";
import { useAsync } from "../hooks";

const DISMISS_KEY = "amendie_gs_dismissed";
const COLLAPSE_KEY = "amendie_gs_collapsed";

/**
 * A dismissible getting-started checklist for new customers. Floats bottom-right (Stripe-style) rather
 * than sitting inline under a page title, so it never crowds the header — and it minimizes to a small
 * corner bubble. Steps auto-tick from live state (has a scraper / has an API key) and deep-link into the
 * relevant tab. Hides itself once the meaningful steps are done, or when dismissed.
 */
export function GettingStarted({ onNav }: { onNav: (tab: "new" | "keys" | "docs") => void }) {
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DISMISS_KEY) === "1");
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSE_KEY) === "1");
  const { data: scrapers } = useAsync<unknown[]>(() => api("/scrapers"), []);
  const { data: keys } = useAsync<unknown[]>(() => api("/api-keys"), []);

  if (dismissed) return null;
  const hasScraper = (scrapers?.length ?? 0) > 0;
  const hasKey = (keys?.length ?? 0) > 0;
  // Nothing left to nudge once they've made a scraper AND a key.
  if (hasScraper && hasKey) return null;

  const steps: { done: boolean; label: string; nav: "new" | "keys" | "docs" }[] = [
    { done: hasScraper, label: "Create your first scraper", nav: "new" },
    { done: hasKey, label: "Create an API key for programmatic access", nav: "keys" },
    { done: false, label: "Explore the API reference", nav: "docs" },
  ];
  const doneCount = steps.filter((s) => s.done).length;

  function dismiss() {
    localStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  }
  function setCollapse(v: boolean) {
    localStorage.setItem(COLLAPSE_KEY, v ? "1" : "0");
    setCollapsed(v);
  }

  // Minimized: a compact corner bubble with progress; click to reopen.
  if (collapsed) {
    return (
      <button className="gs-float gs-bubble" onClick={() => setCollapse(false)} title="Getting started">
        <span className="gs-bubble-title">Getting started</span>
        <span className="gs-bubble-count">
          {doneCount}/{steps.length}
        </span>
      </button>
    );
  }

  return (
    <div className="gs-float getting-started" role="dialog" aria-label="Getting started">
      <div className="gs-head">
        <h3>Getting started</h3>
        <div className="row-gap" style={{ gap: "var(--sp2)" }}>
          <button className="link-btn" onClick={() => setCollapse(true)} title="Minimize" aria-label="Minimize">
            Minimize
          </button>
          <button className="link-btn" onClick={dismiss}>
            Dismiss
          </button>
        </div>
      </div>
      {/* The steps are genuinely sequential, so the thread is content-true, not decoration. */}
      <ul className="gs-steps">
        {steps.map((s) => (
          <li key={s.nav} className={s.done ? "gs-step done" : "gs-step"}>
            <span className="gs-mark" aria-hidden="true">
              <span className="gs-check" />
            </span>
            <button className="gs-link" onClick={() => onNav(s.nav)}>
              {s.label}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
